/**
 * Follow on us.elect.info profile pages (electinfo/sites#435, epic #430).
 *
 * us-pages renders candidate, committee and party pages with their graph
 * entity on <body> (data-entity-id = the canonical UUID, data-entity-type,
 * data-entity-name). Pages are cached per tier and the visitor's follows are
 * not, so the control is client-side:
 *
 *   - a probe through /api/graphql (visitor token, persisted query) asks the
 *     news index for one article about the entity, any scope; nothing is shown
 *     on a page without news;
 *   - with news: "News on elect.news ↗" (the us.elect.news feed filtered to
 *     the entity, new tab) and, where accounts exist, Follow / Following ✓;
 *   - signed in, the button toggles the follow through account-api
 *     (/auth/account/follows, sites#431); signed out it opens the identity
 *     bar's sign-in panel and completes the follow once the visitor is back
 *     signed in (sessionStorage, same tab).
 */

export interface PageEntity {
  id: string;
  type: string;
  name: string;
}

export const CANONICAL_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/** The page's followable entity from <body data-entity-*>, or null (no entity, or an id that is not a canonical UUID). */
export function pageEntity(el: { dataset: DOMStringMap } | null | undefined): PageEntity | null {
  const d = el?.dataset;
  const id = (d?.entityId || '').trim().toLowerCase();
  const type = (d?.entityType || '').trim();
  const name = (d?.entityName || '').trim();
  if (!CANONICAL_UUID.test(id) || !type || !name) return null;
  return { id, type, name };
}

// Fixed document (persisted-queries.js lists it for the visitor-token gate).
// source: OPENSEARCH because canonical filters need the roster index path,
// and preview does not default to it.
export const NEWS_PROBE_QUERY = `query FollowNewsProbe($uuids: [String!]) {
  newsFeed(pageSize: 1, source: OPENSEARCH, scope: ALL, canonical: { entityUuids: $uuids }) {
    items { article { url } }
  }
}`;

type FetchFn = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

/** True when the news index holds at least one article about the entity; false on any failure. */
export async function hasNews(uuid: string, fetchFn: FetchFn = fetch): Promise<boolean> {
  try {
    const res = await fetchFn('/api/graphql', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ query: NEWS_PROBE_QUERY, variables: { uuids: [uuid] } }),
    });
    if (!res.ok) return false;
    const body = await res.json() as { data?: { newsFeed?: { items?: unknown[] } | null } };
    return (body.data?.newsFeed?.items?.length ?? 0) > 0;
  } catch {
    return false;
  }
}

export const NEWS_ORIGIN = 'https://us.elect.news';

/** The us.elect.news feed filtered to the entity, all coverage. */
export function newsUrl(uuid: string): string {
  return `${NEWS_ORIGIN}/?entity=${encodeURIComponent(uuid)}&scope=all`;
}

const FOLLOWS = '/auth/account/follows';
const XHR = { 'X-Requested-With': 'elect.info' };

/** Following state from account-api: true / false, or 'signed-out' (401: the session ended). */
export type FollowState = boolean | 'signed-out';

export async function isFollowing(uuid: string, fetchFn: FetchFn = fetch): Promise<FollowState> {
  const res = await fetchFn(FOLLOWS, { credentials: 'same-origin', cache: 'no-store', redirect: 'manual', headers: { Accept: 'application/json' } });
  if (res.status === 401 || res.type === 'opaqueredirect') return 'signed-out';
  if (!res.ok) throw new Error(`follows ${res.status}`);
  const body = await res.json() as { follows?: { entityId: string }[] };
  return (body.follows ?? []).some(f => f.entityId === uuid);
}

/** Follow (POST) or unfollow (DELETE); the new state, 'signed-out', or an Error with account-api's message. */
export async function setFollow(entity: PageEntity, follow: boolean, fetchFn: FetchFn = fetch): Promise<FollowState> {
  const res = follow
    ? await fetchFn(FOLLOWS, {
      method: 'POST',
      credentials: 'same-origin',
      redirect: 'manual',
      headers: { ...XHR, 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ entityId: entity.id, entityType: entity.type, entityName: entity.name }),
    })
    : await fetchFn(`${FOLLOWS}/${encodeURIComponent(entity.id)}`, {
      method: 'DELETE',
      credentials: 'same-origin',
      redirect: 'manual',
      headers: { ...XHR, Accept: 'application/json' },
    });
  if (res.status === 401 || res.type === 'opaqueredirect') return 'signed-out';
  // Unfollowing what is not followed (another tab got there first) ends in the same state.
  if (!follow && res.status === 404) return false;
  if (!res.ok) {
    let message = 'Could not update your follows. Try again.';
    try {
      const body = await res.json() as { error?: string };
      if (body.error) message = body.error;
    } catch { /* keep the default */ }
    throw new Error(message);
  }
  return follow;
}

/* ---- the follow a signed-out visitor asked for, completed after sign-in ---- */

export const PENDING_KEY = 'ei-follow-pending';
export const PENDING_TTL_MS = 30 * 60 * 1000;

type Store = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

function session(): Store | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function rememberPending(uuid: string, store: Store | null = session(), now = Date.now()): void {
  try {
    store?.setItem(PENDING_KEY, JSON.stringify({ id: uuid, at: now }));
  } catch { /* storage blocked: the visitor clicks Follow again */ }
}

/** True (and forgotten) when the visitor asked to follow this entity before signing in, within the TTL. */
export function takePending(uuid: string, store: Store | null = session(), now = Date.now()): boolean {
  let raw: string | null = null;
  try {
    raw = store?.getItem(PENDING_KEY) ?? null;
  } catch {
    return false;
  }
  if (!raw) return false;
  let p: { id?: string; at?: number } = {};
  try {
    p = JSON.parse(raw);
  } catch { /* drop it */ }
  if (p.id !== uuid) return false;
  try {
    store?.removeItem(PENDING_KEY);
  } catch { /* fine */ }
  return typeof p.at === 'number' && now - p.at >= 0 && now - p.at < PENDING_TTL_MS;
}

/* ---- DOM ---- */

interface SignInUi {
  openSignIn?: (page?: string | null, opts?: { title?: string; lede?: string }) => boolean;
  emailSignInUrl?: (page?: string) => string;
}

interface AuthHost {
  electinfoAuth?: Promise<{ email: string } | null>;
  electinfoIdentityUi?: SignInUi;
}

const STYLE = `
.ei-follow{display:flex;flex-wrap:wrap;align-items:center;gap:8px 14px;margin:10px 0 0}
.ei-follow__btn{display:inline-flex;align-items:center;gap:6px;padding:5px 14px;border:1px solid #2c5282;border-radius:999px;
  background:#2c5282;color:#fff;font:inherit;font-size:.9rem;font-weight:600;cursor:pointer;line-height:1.3}
.ei-follow__btn:hover{background:#1a365d;border-color:#1a365d}
.ei-follow__btn[aria-pressed=true]{background:#fff;color:#2c5282}
.ei-follow__btn[aria-pressed=true]:hover{background:#ebf4ff}
.ei-follow__btn:disabled{opacity:.6;cursor:progress}
.ei-follow__btn:focus-visible,.ei-follow__news:focus-visible{outline:2px solid #ed8936;outline-offset:2px}
.ei-follow__news{font-size:.9rem;font-weight:600;color:#2c5282}
.ei-follow__msg{flex-basis:100%;margin:0;font-size:.85rem;color:#c53030}
.ei-follow__msg:empty{display:none}
header .ei-follow__btn{background:#fff;border-color:#fff;color:#1a365d}
header .ei-follow__btn:hover{background:#ebf4ff;border-color:#ebf4ff}
header .ei-follow__btn[aria-pressed=true]{background:transparent;border-color:rgba(255,255,255,.75);color:#fff}
header .ei-follow__btn[aria-pressed=true]:hover{background:rgba(255,255,255,.12)}
header .ei-follow__news{color:#fff;text-decoration:underline;text-underline-offset:3px}
header .ei-follow__msg{color:#fed7d7}`;

function ensureStyle(doc: Document): void {
  if (doc.getElementById('ei-follow-css')) return;
  const style = doc.createElement('style');
  style.id = 'ei-follow-css';
  style.textContent = STYLE;
  doc.head.appendChild(style);
}

export const FOLLOW_LABEL = 'Follow';
export const FOLLOWING_LABEL = 'Following ✓';

function paint(button: HTMLButtonElement, following: boolean, name: string): void {
  button.textContent = following ? FOLLOWING_LABEL : FOLLOW_LABEL;
  button.setAttribute('aria-pressed', following ? 'true' : 'false');
  button.title = following ? `Unfollow ${name}` : `Follow ${name}'s news on your elect.news Digest`;
}

/** Mount the control on a profile page with news. Returns the container, or null when the page has no entity or news. */
export async function initFollowControl(doc: Document = document, fetchFn: FetchFn = (i, o) => fetch(i, o)): Promise<HTMLElement | null> {
  const entity = pageEntity(doc.body);
  if (!entity) return null;
  const host = (doc.defaultView ?? window) as unknown as AuthHost;
  const [news, auth] = await Promise.all([
    hasNews(entity.id, fetchFn),
    Promise.resolve(host.electinfoAuth ?? null).catch(() => null),
  ]);
  if (!news) return null;

  ensureStyle(doc);
  const box = doc.createElement('div');
  box.className = 'ei-follow';
  box.id = 'ei-follow';
  const link = doc.createElement('a');
  link.className = 'ei-follow__news';
  link.href = newsUrl(entity.id);
  link.target = '_blank';
  link.rel = 'noopener';
  link.textContent = 'News on elect.news ↗';
  const msg = doc.createElement('p');
  msg.className = 'ei-follow__msg';
  msg.setAttribute('role', 'status');

  // auth null: no accounts on this host (Authentik off or unreachable), so only the link.
  if (auth) {
    const button = doc.createElement('button');
    button.type = 'button';
    button.className = 'ei-follow__btn';
    button.dataset.entityId = entity.id;
    let signedIn = Boolean(auth.email);
    let following = false;
    paint(button, false, entity.name);

    const signIn = (): void => {
      rememberPending(entity.id);
      const ui = host.electinfoIdentityUi;
      const opened = ui?.openSignIn?.(doc.location.href, {
        title: `Sign in to follow ${entity.name}`,
        lede: 'Follow sends their news to your Digest on us.elect.news. It’s free with an elect.info account.',
      });
      if (!opened && ui?.emailSignInUrl) doc.location.assign(ui.emailSignInUrl(doc.location.href));
    };

    const apply = async (want: boolean): Promise<void> => {
      button.disabled = true;
      msg.textContent = '';
      try {
        const state = await setFollow(entity, want, fetchFn);
        if (state === 'signed-out') {
          signedIn = false;
          signIn();
          return;
        }
        following = state;
        paint(button, following, entity.name);
      } catch (e) {
        msg.textContent = e instanceof Error ? e.message : String(e);
      } finally {
        button.disabled = false;
      }
    };

    button.addEventListener('click', () => {
      if (!signedIn) signIn();
      else void apply(!following);
    });

    if (signedIn) {
      try {
        const state = await isFollowing(entity.id, fetchFn);
        if (state === 'signed-out') signedIn = false;
        else following = state;
      } catch { /* show Follow; a click reports the error */ }
      paint(button, following, entity.name);
      // Back from the sign-in the visitor started on this page: finish their follow.
      if (signedIn && takePending(entity.id)) {
        if (!following) await apply(true);
      }
    }
    box.append(button);
  }
  box.append(link, msg);

  const header = doc.querySelector('body > header');
  if (header) header.append(box);
  else doc.body.prepend(box);
  return box;
}
